import { useEffect, useState } from "react";
import { Label } from "@radix-ui/react-label";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Input } from "./ui/input";
import './LoginForm.css';
import { useNavigate } from "react-router-dom";
import { AuthService } from "../services/auth.service";
import { customSwalStyling, DataService } from "../services/data.service";
import { debugModeActive } from "../lib/utils";
import { AuthModel } from "../models/auth.model";
import Swal from "sweetalert2";

type Mode = 'main' | 'password' | 'verify-code' | 'forgot' | 'reset-verify'

const isEmailShape = (v: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim())
const isCodeShape = (v: string) => /^\d{6}$/.test(v.trim())
const isPasswordOk = (v: string) => v.length >= 8 && /\d/.test(v)

export function LoginForm() {
  const navigate = useNavigate();
  const [mode, setMode] = useState<Mode>('main');
  const [isLoading, setIsLoading] = useState(false);

  // Shared identifier — accepts either an email or a legacy user_name
  // (the backend branches by '@' shape; field stays labeled "email").
  const [signinId, setSigninId] = useState("");
  const [signinPassword, setSigninPassword] = useState("");
  const [signinInviteCode, setSigninInviteCode] = useState("");
  const [showSigninInvite, setShowSigninInvite] = useState(false);
  const [signinCode, setSigninCode] = useState("");

  const [resetEmail, setResetEmail] = useState("");
  const [resetCode, setResetCode] = useState("");
  const [resetNewPassword, setResetNewPassword] = useState("");

  const idIsEmail = isEmailShape(signinId);
  const idLooksUsable = idIsEmail || signinId.trim().length >= 3;
  const resetEmailValid = isEmailShape(resetEmail);
  const resetCodeValid = isCodeShape(resetCode);
  const resetPasswordValid = isPasswordOk(resetNewPassword);

  // Native-app shell injection: keep backwards compatibility with the existing
  // global so iOS/Android wrappers that already use it keep working.
  //@ts-ignore
  window.MobileAppLogin = async (auth: AuthModel) => {
    localStorage.setItem('is_mobile', '1')
    Swal.fire({
      title: "Please wait",
      text: "We are processing your request!",
      customClass: customSwalStyling,
      didOpen: () => Swal.showLoading()
    })
    setTimeout(() => {
      AuthService.saveAuth(auth)
      Swal.close()
      navigate('/dashboard', { replace: true })
    }, 2000)
  }

  // Convenience for native-app shells that scripted into the old form.
  //@ts-ignore
  window.DoLogin = async (username: string, password: string) => {
    if (await DataService.login(username, password)) {
      navigate("/dashboard", { replace: true })
    }
  }

  useEffect(() => {
    document.body.style.background = "white";
    if (AuthService.hasAuth()) {
      navigate('/dashboard', { replace: true })
    }
  }, [navigate])

  // ============================================================
  // Email-code path (default; new + returning users converge here)
  // ============================================================
  async function handleRequestCode(e?: React.FormEvent) {
    if (e) e.preventDefault();
    if (!idIsEmail) return;
    setIsLoading(true);
    Swal.fire({ title: "Please wait", text: "Sending code…", customClass: customSwalStyling, didOpen: () => Swal.showLoading() })
    try {
      await DataService.requestSigninCode(signinId.trim(), signinInviteCode.trim());
      setSigninCode('');
      Swal.close();
      Swal.fire({ icon: "success", title: "Code Sent", text: "If this email is valid, a sign-in code has been sent.", customClass: customSwalStyling })
      setMode('verify-code');
    } catch (err: any) {
      Swal.fire({ icon: "error", title: "Error", text: err?.message || "Failed to send code", customClass: customSwalStyling })
    } finally {
      setIsLoading(false);
    }
  }

  async function handleVerifyCode(e?: React.FormEvent) {
    if (e) e.preventDefault();
    if (!signinId || !isCodeShape(signinCode)) return;
    setIsLoading(true);
    Swal.fire({ title: "Please wait", text: "Verifying…", customClass: customSwalStyling, didOpen: () => Swal.showLoading() })
    try {
      const { isNewUser } = await DataService.verifySigninCode(signinId.trim(), signinCode.trim());
      Swal.close();
      // A human submitting the login form is by definition in a browser, not
      // the native WebView (which uses /auto-login or window.MobileAppLogin).
      // Clear any stale is_mobile=1 a previous WebView session left on this
      // device so the dashboard logout button reappears.
      localStorage.removeItem('is_mobile')
      await Swal.fire({
        icon: "success",
        title: isNewUser ? "Welcome to WellNuo!" : "Welcome back",
        text: isNewUser ? "Your account is ready." : "Signed in successfully.",
        timer: 1800,
        timerProgressBar: true,
        customClass: customSwalStyling,
      });
      navigate('/dashboard', { replace: true });
    } catch (err: any) {
      Swal.fire({ icon: "error", title: "Invalid Code", text: err?.message || "Code is invalid or expired", customClass: customSwalStyling })
    } finally {
      setIsLoading(false);
    }
  }

  // ============================================================
  // Password path (with pivot to OTP on email-shaped failure)
  // ============================================================
  async function handlePasswordSignin(e?: React.FormEvent) {
    if (e) e.preventDefault();
    if (!signinId || !signinPassword) return;
    setIsLoading(true);
    // For email-shaped input, suppress the generic "Login Failed" toast and
    // offer a pivot to the OTP path. Username input keeps the standard error.
    const useSilent = idIsEmail;
    try {
      const ok = await DataService.login(signinId.trim(), signinPassword, { silent: useSilent });
      if (ok) {
        // See handleVerifyCode: a form-driven sign-in is always a browser
        // session — clear any stale WebView flag.
        localStorage.removeItem('is_mobile')
        navigate('/dashboard', { replace: true });
        return;
      }
      if (useSilent) {
        const choice = await Swal.fire({
          icon: "info",
          title: "Couldn't sign in",
          text: "Want us to email you a sign-in code instead?",
          showCancelButton: true,
          confirmButtonText: "Email me a code",
          cancelButtonText: "Try again",
          customClass: customSwalStyling,
        });
        if (choice.isConfirmed) {
          await handleRequestCode();
        }
      }
    } finally {
      setIsLoading(false);
    }
  }

  // ============================================================
  // Forgot password (code-then-reset)
  // ============================================================
  function goToForgot() {
    setResetEmail(idIsEmail ? signinId.trim() : '');
    setResetCode('');
    setResetNewPassword('');
    setMode('forgot');
  }

  async function handleRequestReset(e?: React.FormEvent) {
    if (e) e.preventDefault();
    if (!resetEmailValid) return;
    setIsLoading(true);
    Swal.fire({ title: "Please wait", text: "Sending reset code…", customClass: customSwalStyling, didOpen: () => Swal.showLoading() })
    try {
      await DataService.requestPasswordReset(resetEmail.trim());
      setResetCode('');
      setResetNewPassword('');
      Swal.close();
      Swal.fire({ icon: "success", title: "Code Sent", text: "If this email is registered, you will receive a reset code.", customClass: customSwalStyling })
      setMode('reset-verify');
    } catch (err: any) {
      Swal.fire({ icon: "error", title: "Error", text: err?.message || "Failed to send reset code", customClass: customSwalStyling })
    } finally {
      setIsLoading(false);
    }
  }

  async function handleResetVerify(e?: React.FormEvent) {
    if (e) e.preventDefault();
    if (!resetCodeValid || !resetPasswordValid) return;
    setIsLoading(true);
    Swal.fire({ title: "Please wait", text: "Resetting password…", customClass: customSwalStyling, didOpen: () => Swal.showLoading() })
    try {
      await DataService.verifyResetCode(resetEmail.trim(), resetCode.trim(), resetNewPassword);
      Swal.close();
      await Swal.fire({ icon: "success", title: "Password Reset", text: "Your password has been updated. You can now sign in.", customClass: customSwalStyling });
      setSigninId(resetEmail.trim());
      setSigninPassword('');
      setMode('password');
    } catch (err: any) {
      Swal.fire({ icon: "error", title: "Error", text: err?.message || "Failed to reset password", customClass: customSwalStyling })
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div className="login-container">
      <Card className="login-card">
        <div className="login-image">
          <img src="/assets/login-backgroud.png" alt="Login Background" />
        </div>
        <div className="card-white">
          <CardHeader>
            <CardTitle className="card-title">WellNuo</CardTitle>
            <CardDescription className="card-desc">So you know they are well</CardDescription>
          </CardHeader>
          <CardContent>

            {/* ===== Main: email-first entry ===== */}
            {mode === 'main' && (
              <form className="login-form" onSubmit={handleRequestCode}>
                <p className="text-center text-sm text-gray-500">Enter your email to sign in or create an account</p>
                <div className="form-group">
                  <Label htmlFor="si-id">Email</Label>
                  <Input
                    id="si-id"
                    type="text"
                    placeholder="email@example.com"
                    autoComplete="username"
                    value={signinId}
                    onChange={(e) => setSigninId(e.target.value)}
                  />
                  {!!signinId && !idLooksUsable && (
                    <small className="text-gray-500">Enter a valid email. (Legacy username accounts can sign in with password below.)</small>
                  )}
                </div>

                {!showSigninInvite ? (
                  <button
                    type="button"
                    className="text-sm text-gray-500 underline self-center"
                    onClick={() => setShowSigninInvite(true)}
                  >
                    I have an invite code
                  </button>
                ) : (
                  <div className="form-group">
                    <Label htmlFor="si-invite">Invite Code</Label>
                    <Input
                      id="si-invite"
                      type="text"
                      placeholder="Enter invite code"
                      maxLength={32}
                      value={signinInviteCode}
                      onChange={(e) => setSigninInviteCode(e.target.value)}
                    />
                  </div>
                )}

                <Button type="submit" className="login-button bg-blue-500 hover:bg-blue-600" disabled={!idIsEmail || isLoading}>
                  Email me a sign-in code
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="login-button"
                  disabled={!idLooksUsable || isLoading}
                  onClick={() => setMode('password')}
                >
                  Sign in with password
                </Button>

                <p className="text-center text-xs text-gray-500">
                  First time? Just enter your email — we'll send you a code.
                </p>
                <p className="text-center text-xs text-gray-400">
                  By continuing you agree to our{' '}
                  <a href="https://app.wellnuo.com/terms" target="_blank" rel="noopener noreferrer" className="underline">Terms of Service</a>
                  {' '}and{' '}
                  <a href="https://app.wellnuo.com/privacy" target="_blank" rel="noopener noreferrer" className="underline">Privacy Policy</a>.
                </p>
              </form>
            )}

            {/* ===== Password sign-in ===== */}
            {mode === 'password' && (
              <form className="login-form" onSubmit={handlePasswordSignin}>
                <p className="text-center text-sm text-gray-500">Use the password associated with your account</p>
                <div className="form-group">
                  <Label htmlFor="si-id-pw">Email or username</Label>
                  <Input
                    id="si-id-pw"
                    type="text"
                    placeholder="email@example.com or username"
                    autoComplete="username"
                    value={signinId}
                    onChange={(e) => setSigninId(e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <Label htmlFor="si-pw">Password</Label>
                  <Input
                    id="si-pw"
                    type="password"
                    placeholder="Enter your password"
                    autoComplete="current-password"
                    value={signinPassword}
                    onChange={(e) => setSigninPassword(e.target.value)}
                  />
                </div>
                <button type="button" className="text-sm text-blue-500 underline self-end" onClick={goToForgot}>
                  Forgot password?
                </button>
                <Button type="submit" className="login-button bg-blue-500 hover:bg-blue-600" disabled={!idLooksUsable || !signinPassword || isLoading}>
                  Sign In
                </Button>
                <Button type="button" variant="outline" className="login-button" onClick={() => setMode('main')}>
                  Use a different sign-in method
                </Button>
                <p className="text-center text-xs text-gray-500">
                  Username accounts: password reset requires the email on file.
                </p>
              </form>
            )}

            {/* ===== Verify sign-in code ===== */}
            {mode === 'verify-code' && (
              <form className="login-form" onSubmit={handleVerifyCode}>
                <p className="text-center text-sm text-gray-500">
                  We sent a 6-digit code to <strong>{signinId}</strong>
                </p>
                <div className="form-group">
                  <Label htmlFor="si-code">Verification Code</Label>
                  <Input
                    id="si-code"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    autoComplete="one-time-code"
                    placeholder="000000"
                    maxLength={6}
                    value={signinCode}
                    onChange={(e) => setSigninCode(e.target.value)}
                  />
                </div>
                <Button type="submit" className="login-button bg-blue-500 hover:bg-blue-600" disabled={!isCodeShape(signinCode) || isLoading}>
                  Verify Code
                </Button>
                <Button type="button" variant="link" className="login-button" disabled={isLoading} onClick={() => handleRequestCode()}>
                  Resend code
                </Button>
                <Button type="button" variant="outline" className="login-button" onClick={() => setMode('main')}>
                  Back
                </Button>
              </form>
            )}

            {/* ===== Forgot password: request code ===== */}
            {mode === 'forgot' && (
              <form className="login-form" onSubmit={handleRequestReset}>
                <p className="text-center text-sm text-gray-500">We'll send a reset code to your email</p>
                <div className="form-group">
                  <Label htmlFor="reset-email">Email</Label>
                  <Input
                    id="reset-email"
                    type="email"
                    autoComplete="email"
                    placeholder="Enter your email"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                  />
                </div>
                <Button type="submit" className="login-button bg-blue-500 hover:bg-blue-600" disabled={!resetEmailValid || isLoading}>
                  Send Reset Code
                </Button>
                <Button type="button" variant="outline" className="login-button" onClick={() => setMode('password')}>
                  Back to sign in
                </Button>
              </form>
            )}

            {/* ===== Forgot password: verify code + new password ===== */}
            {mode === 'reset-verify' && (
              <form className="login-form" onSubmit={handleResetVerify}>
                <p className="text-center text-sm text-gray-500">
                  Enter the code sent to <strong>{resetEmail}</strong> and your new password
                </p>
                <div className="form-group">
                  <Label htmlFor="reset-code">Reset Code</Label>
                  <Input
                    id="reset-code"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    autoComplete="one-time-code"
                    placeholder="000000"
                    maxLength={6}
                    value={resetCode}
                    onChange={(e) => setResetCode(e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <Label htmlFor="reset-newpw">New Password</Label>
                  <Input
                    id="reset-newpw"
                    type="password"
                    autoComplete="new-password"
                    placeholder="At least 8 characters with a number"
                    value={resetNewPassword}
                    onChange={(e) => setResetNewPassword(e.target.value)}
                  />
                </div>
                <Button type="submit" className="login-button bg-blue-500 hover:bg-blue-600" disabled={!resetCodeValid || !resetPasswordValid || isLoading}>
                  Reset Password
                </Button>
                <Button type="button" variant="link" className="login-button" disabled={isLoading} onClick={() => handleRequestReset()}>
                  Resend code
                </Button>
                <Button type="button" variant="outline" className="login-button" onClick={() => setMode('password')}>
                  Back to sign in
                </Button>
              </form>
            )}

          </CardContent>
        </div>
        {debugModeActive() && <p className="text-center text-gray-400 mt-1">Build Version: {process.env.REACT_APP_VERSION}</p>}
      </Card>
    </div>
  );
}
